import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { CASE_STUDIES } from "@/lib/siteData";

export default function CaseStudies() {
  return (
    <div data-testid="case-studies-page">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24">
          <p className="vd-eyebrow">Case Studies</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900 max-w-4xl">
            Selected work that reflects how we deliver{" "}
            <span className="text-teal-700">execution at scale</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed">
            Anonymized examples illustrating recruitment expertise, multi-market
            coordination, and the operational rigour behind successful research
            programs.
          </p>
        </div>
      </section>

      {/* CASES */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-24 space-y-16">
          {CASE_STUDIES.map((cs, i) => (
            <motion.article
              key={cs.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              data-testid={`case-study-${i}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 border-b border-slate-200 last:border-b-0 last:pb-0"
            >
              <div className="lg:col-span-4">
                <p className="vd-eyebrow">Case {String(i + 1).padStart(2, "0")}</p>
                <p className="mt-3 text-sm text-slate-500">{cs.industry}</p>
                <div className="mt-8">
                  <div className="font-display text-5xl md:text-6xl font-semibold text-slate-900 leading-none">
                    {cs.metric}
                  </div>
                  <div className="mt-3 text-[12px] uppercase tracking-[0.2em] text-teal-700">
                    {cs.metricLabel}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-8">
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight leading-tight">
                  {cs.title}
                </h2>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <p className="vd-eyebrow">Challenge</p>
                    <p className="mt-3 text-slate-700 leading-relaxed">
                      {cs.challenge}
                    </p>
                  </div>
                  <div>
                    <p className="vd-eyebrow">Approach</p>
                    <p className="mt-3 text-slate-700 leading-relaxed">
                      {cs.approach}
                    </p>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="vd-eyebrow">Outcomes</p>
                  <ul className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {cs.outcomes.map((o) => (
                      <li
                        key={o}
                        className="flex items-start gap-2 border border-slate-200 rounded-md px-4 py-3 bg-white"
                      >
                        <CheckCircle2 className="h-4 w-4 text-teal-700 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-800">{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0b192c] text-white relative overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg-dark opacity-50 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24 text-center">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
            Your project, our next case study
          </p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-3xl mx-auto">
            From single studies to multi-country programs — let’s build something measurable together.
          </h2>
          <Link
            to="/contact"
            data-testid="cases-cta"
            className="mt-8 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-7 py-3.5 rounded-md transition-colors shadow-sm"
          >
            Start a conversation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
